import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { Check } from 'lucide-react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { toast } from '@/components/ui/toast-store'
import { useCompletePathStep } from '@/features/courses/api/mutations'
import { useLearningPath, useSuspensePathStep } from '@/features/courses/api/queries'
import { formatSeconds } from '@/features/courses/lib/duration'
import { cn } from '@/lib/utils'
import { parseId } from '@/screens/courses/lib/params'
import { TERM_CHIP_LINK } from '@/screens/courses/components/dictionary/term-chip'
import {
  LEARN_ASIDE,
  LEARN_COLUMNS,
  LEARN_PAGE,
} from '@/screens/courses/components/learn/learn-page'
import LearnHead, { LearnHeadSkeleton } from '@/screens/courses/components/learn/LearnHead'
import { DoneCard, NextCard } from '@/screens/courses/components/path/PathStepAside'
import PathStepVideo from '@/screens/courses/components/path/PathStepVideo'
import StepBar from '@/screens/courses/components/path/StepBar'
import { loadPathStep } from '@/screens/courses/PathStepScreen.loader'

function StepNotFound() {
  return (
    <ScreenLayout {...LEARN_PAGE} pageTitle="Step not found">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Step not found"
        description="This step may have been removed, or the link is wrong."
        action={
          <Link to="/courses/path" className={stateActionClass('page')}>
            Back to your path
          </Link>
        }
      />
    </ScreenLayout>
  )
}

function PathStepPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading this step…"
      header={<LearnHeadSkeleton back />}
    >
      <div aria-hidden="true" className={LEARN_COLUMNS}>
        <div className="flex flex-col gap-8">
          <Skeleton className="aspect-video w-full rounded-lg" />
          <div className="flex flex-col gap-3">
            <div className="flex min-h-9 items-center">
              <Skeleton className="h-5 w-32 rounded-xs" />
            </div>
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        </div>
        <div className={LEARN_ASIDE}>
          <div className="flex flex-col gap-3">
            <div className="flex min-h-9 items-center">
              <Skeleton className="h-5 w-36 rounded-xs" />
            </div>
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex min-h-9 items-center">
              <Skeleton className="h-5 w-24 rounded-xs" />
            </div>
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function PathStepError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      fullWidth
      header={<LearnHeadSkeleton back />}
      title="This step did not load"
    />
  )
}

export const Route = createFileRoute('/_app/courses/path/$stepId')({
  loader: loadPathStep,
  pendingComponent: PathStepPending,
  errorComponent: PathStepError,
  component: PathStepRoute,
})

/** An unparseable id never reaches the suspense read (the loader skipped it too). */
function PathStepRoute() {
  const stepId = parseId(Route.useParams().stepId)
  if (stepId === null) return <StepNotFound />
  return <PathStepScreen key={stepId} stepId={stepId} />
}

/**
 * Learn · one step of the learning path (`GET /path/steps/{id}/` +
 * `POST …/complete/`). `null` (`LEARNING_STEP_NOT_FOUND`) is `StepNotFound`,
 * not a failed page.
 *
 * Two columns: the video, what the step teaches and its words on the left;
 * the one control that finishes it and the step after it on the right. The
 * head carries "Step N of M" above the title, and the path's own bar at the
 * right; the step's length is on the video's cover (rest:ln, mock `ln-head`).
 *
 * "Step N of M" reads this step's own RANK in the path's step list
 * (`useLearningPath`, a plain query the loader only prefetches — see its own
 * doc comment on why `order` alone cannot answer this), not `step.order`
 * itself. The path fetch is still in flight, unauthored, or failed on some
 * visits, and the header just carries on without that line (and the bar)
 * then rather than showing a number that may not be this step's true
 * position.
 */
function PathStepScreen({ stepId }: { stepId: number }) {
  const { data: step } = useSuspensePathStep(stepId)
  const path = useLearningPath().data
  const completeStep = useCompletePathStep()
  const navigate = Route.useNavigate()
  if (step === null) return <StepNotFound />

  const rank = (() => {
    if (path === null || path === undefined) return null
    const index = path.steps.findIndex((entry) => entry.id === stepId)
    return index === -1 ? null : { position: index + 1, total: path.total }
  })()

  const next =
    step.nextStepId === null
      ? null
      : (path?.steps.findIndex((entry) => entry.id === step.nextStepId) ?? -1)
  const nextEntry = next === null || next === -1 ? null : path?.steps[next]
  const nextLine = [
    next === null || next === -1 ? null : `Step ${String(next + 1)}`,
    nextEntry === null || nextEntry === undefined ? null : formatSeconds(nextEntry.durationSeconds),
  ]
    .filter((part): part is string => part !== null)
    .join(', ')

  const kicker =
    rank === null ? undefined : `Step ${String(rank.position)} of ${String(rank.total)}`

  const complete = () => {
    completeStep.mutate(stepId, {
      onSuccess: () => {
        // The tick's own promise: finishing a step opens the one after it.
        if (step.nextStepId !== null) {
          void navigate({
            to: '/courses/path/$stepId',
            params: { stepId: String(step.nextStepId) },
          })
        }
      },
      onError: () => {
        toast.error("Couldn't mark this step complete. Try again.")
      },
    })
  }

  return (
    <ScreenLayout
      {...LEARN_PAGE}
      header={
        <LearnHead
          title={step.title}
          back="/courses/path"
          kicker={kicker}
          sub={step.subtitle ?? undefined}
          aside={
            rank === null || path === null || path === undefined ? undefined : (
              <StepBar
                fixed
                className="max-sm:hidden"
                completed={path.completed}
                total={path.total}
                label={`${path.title}: steps done`}
              />
            )
          }
        />
      }
      pageTitle={step.title}
    >
      <div className={LEARN_COLUMNS}>
        <div className="flex min-w-0 flex-col gap-8">
          <PathStepVideo
            source={step.source}
            title={step.title}
            length={formatSeconds(step.durationSeconds)}
          />

          {step.learnPoints.length === 0 ? null : (
            <section className="flex flex-col gap-3">
              <SectionHead title="You'll learn" size="card" />
              <ul className={cn(SECTION_CARD, 'flex flex-col gap-3 p-5')}>
                {step.learnPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-body-m text-foreground">
                    <span className="flex h-5 shrink-0 items-center">
                      <Check className="size-4 text-accent" aria-hidden="true" />
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {step.glossaryTerms.length === 0 ? null : (
            <section className="flex flex-col gap-3">
              <SectionHead
                title="Words to keep"
                size="card"
                action={<SeeAllLink to="/learn/dictionary">Open the dictionary</SeeAllLink>}
              />
              {/* `glossaryTerms` are display words ("Kitta"), not dictionary
                  slugs — each chip opens the dictionary's own search rather
                  than a term page that may not exist under that spelling. */}
              <div className="flex flex-wrap gap-2">
                {step.glossaryTerms.map((term) => (
                  <Link
                    key={term}
                    to="/learn/dictionary"
                    search={{ q: term }}
                    className={TERM_CHIP_LINK}
                  >
                    {term}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className={LEARN_ASIDE}>
          <DoneCard
            position={rank?.position ?? null}
            completed={step.completed}
            hasNext={step.nextStepId !== null}
            pending={completeStep.isPending}
            onComplete={complete}
          />
          {step.nextStepId === null ? null : (
            <NextCard nextStepId={step.nextStepId} title={step.nextStepTitle} line={nextLine} />
          )}
        </div>
      </div>
    </ScreenLayout>
  )
}
